// dashboard/components/Transactions.jsx
import React, { useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Pagination,
  Avatar,
} from '@mui/material';
import {
  Search,
  FilterList,
  Receipt,
  TrendingUp,
  TrendingDown,
  AttachMoney,
  CreditCard,
  AccountBalance,
  ArrowUpward,
  ArrowDownward,
  Download,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: 'rgba(0,0,0,0.02)',
  },
}));

const Transactions = () => {
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);

  const transactions = [
    {
      id: 1,
      type: 'purchase',
      category: 'Course',
      description: 'Advanced Forex Trading Course',
      amount: -299.99,
      date: '2024-01-15 14:30:00',
      status: 'completed',
      method: 'Credit Card',
    },
    {
      id: 2,
      type: 'subscription',
      category: 'Subscription',
      description: 'VIP Telegram Signals - Monthly',
      amount: -99.99,
      date: '2024-01-10 09:15:00',
      status: 'completed',
      method: 'PayPal',
    },
    {
      id: 3,
      type: 'deposit',
      category: 'Deposit',
      description: 'Wallet Deposit',
      amount: 500.00,
      date: '2024-01-08 11:45:00',
      status: 'completed',
      method: 'Bank Transfer',
    },
    {
      id: 4,
      type: 'withdrawal',
      category: 'Withdrawal',
      description: 'Withdrawal to Bank Account',
      amount: -200.00,
      date: '2024-01-06 16:20:00',
      status: 'pending',
      method: 'Bank Transfer',
    },
    {
      id: 5,
      type: 'purchase',
      category: 'Course',
      description: 'Crypto Trading Mastery 2024',
      amount: -199.99,
      date: '2024-01-05 13:00:00',
      status: 'completed',
      method: 'Credit Card',
    },
    {
      id: 6,
      type: 'deposit',
      category: 'Deposit',
      description: 'Crypto Deposit',
      amount: 1000.00,
      date: '2024-01-03 08:30:00',
      status: 'failed',
      method: 'Crypto',
    },
    {
      id: 7,
      type: 'subscription',
      category: 'Subscription',
      description: 'Premium Trading Course - Monthly',
      amount: -49.99,
      date: '2024-01-01 10:00:00',
      status: 'completed',
      method: 'Credit Card',
    },
    {
      id: 8,
      type: 'withdrawal',
      category: 'Withdrawal',
      description: 'Withdrawal to Crypto Wallet',
      amount: -150.00,
      date: '2023-12-28 14:45:00',
      status: 'completed',
      method: 'Crypto',
    },
  ];

  const getStatusColor = (status) => {
    switch(status) {
      case 'completed': return '#2ecc71';
      case 'pending': return '#f39c12';
      case 'failed': return '#e74c3c';
      default: return '#95a5a6';
    }
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'deposit': return <TrendingUp sx={{ color: '#2ecc71' }} />;
      case 'withdrawal': return <TrendingDown sx={{ color: '#e74c3c' }} />;
      case 'purchase': return <Receipt sx={{ color: '#3498db' }} />;
      case 'subscription': return <AttachMoney sx={{ color: '#9b59b6' }} />;
      default: return <Receipt />;
    }
  };

  const getTypeColor = (type) => {
    switch(type) {
      case 'deposit': return '#2ecc71';
      case 'withdrawal': return '#e74c3c';
      case 'purchase': return '#3498db';
      case 'subscription': return '#9b59b6';
      default: return '#95a5a6';
    }
  };

  const filteredTransactions = transactions.filter(transaction => {
    const matchesFilter = filter === 'all' || transaction.type === filter;
    const matchesSearch = transaction.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         transaction.method.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const summary = {
    totalDeposits: transactions.filter(t => t.type === 'deposit' && t.status === 'completed').reduce((sum, t) => sum + t.amount, 0),
    totalWithdrawals: transactions.filter(t => t.type === 'withdrawal' && t.status === 'completed').reduce((sum, t) => sum + Math.abs(t.amount), 0),
    totalSpent: transactions.filter(t => t.type === 'purchase' && t.status === 'completed').reduce((sum, t) => sum + Math.abs(t.amount), 0),
    totalSubscriptions: transactions.filter(t => t.type === 'subscription' && t.status === 'completed').reduce((sum, t) => sum + Math.abs(t.amount), 0),
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#0a1628' }}>
            Transactions
          </Typography>
          <Typography variant="body2" sx={{ color: '#7f8c8d', mt: 0.5 }}>
            View and manage your transaction history
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Download />}
          sx={{ bgcolor: '#0a1628', '&:hover': { bgcolor: '#1a2a4a' }, borderRadius: 2 }}
        >
          Export CSV
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={3}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #eef2f7', textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: '#95a5a6' }}>Total Deposits</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#2ecc71' }}>
              ${summary.totalDeposits.toFixed(2)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #eef2f7', textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: '#95a5a6' }}>Total Withdrawals</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#e74c3c' }}>
              ${summary.totalWithdrawals.toFixed(2)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #eef2f7', textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: '#95a5a6' }}>Total Spent</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#3498db' }}>
              ${summary.totalSpent.toFixed(2)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #eef2f7', textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: '#95a5a6' }}>Subscriptions</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#9b59b6' }}>
              ${summary.totalSubscriptions.toFixed(2)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #eef2f7', mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="Search transactions..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flex: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: '#95a5a6' }} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              label="Type"
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="deposit">Deposits</MenuItem>
              <MenuItem value="withdrawal">Withdrawals</MenuItem>
              <MenuItem value="purchase">Purchases</MenuItem>
              <MenuItem value="subscription">Subscriptions</MenuItem>
            </Select>
          </FormControl>
          <IconButton sx={{ color: '#7f8c8d' }}>
            <FilterList />
          </IconButton>
        </Box>
      </Paper>

      {/* Transactions Table */}
      <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: '1px solid #eef2f7' }}>
        <Table>
          <TableHead sx={{ bgcolor: '#f8f9fa' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, color: '#0a1628' }}>Transaction</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#0a1628' }}>Category</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#0a1628' }}>Method</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#0a1628' }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#0a1628' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#0a1628', textAlign: 'right' }}>Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTransactions.map((transaction) => (
              <StyledTableRow key={transaction.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: `${getTypeColor(transaction.type)}20`, width: 36, height: 36 }}>
                      {getTypeIcon(transaction.type)}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#0a1628' }}>
                        {transaction.description}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#95a5a6' }}>
                        #{transaction.id}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={transaction.category}
                    size="small"
                    sx={{
                      bgcolor: `${getTypeColor(transaction.type)}15`,
                      color: getTypeColor(transaction.type),
                      fontWeight: 600,
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CreditCard sx={{ fontSize: 16, color: '#95a5a6' }} />
                    <Typography variant="body2" sx={{ color: '#0a1628' }}>
                      {transaction.method}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ color: '#0a1628' }}>
                    {new Date(transaction.date).toLocaleDateString()}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#95a5a6', display: 'block' }}>
                    {new Date(transaction.date).toLocaleTimeString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={transaction.status}
                    size="small"
                    sx={{
                      bgcolor: `${getStatusColor(transaction.status)}20`,
                      color: getStatusColor(transaction.status),
                      fontWeight: 600,
                      textTransform: 'capitalize',
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      textAlign: 'right',
                      color: transaction.amount > 0 ? '#2ecc71' : '#e74c3c',
                    }}
                  >
                    {transaction.amount > 0 ? '+' : ''}{transaction.amount.toFixed(2)}
                  </Typography>
                </TableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <Pagination count={Math.ceil(filteredTransactions.length / 5)} page={page} onChange={(e, p) => setPage(p)} color="primary" />
        </Box>
      </TableContainer>
    </Box>
  );
};

export default Transactions;